import axios from '@/lib/axios'
import { buildAdminRequestConfig } from '@/lib/organization-scoping'
import {
  customerImportActiveAttemptResponseSchema,
  customerImportAttemptSchema,
  customerImportResponseSchema,
  productImportActiveAttemptResponseSchema,
  productImportAttemptSchema,
  productImportResponseSchema,
} from './imports.schema'
import type {
  CustomerImportAttempt,
  CustomerImportFileInput,
  CustomerImportInput,
  CustomerImportResponse,
  CustomerImportSourceSystem,
  ProductImportAttempt,
  ProductImportFileInput,
  ProductImportInput,
  ProductImportResponse,
} from './imports.types'

export interface ImportOrganizationScope {
  organizationId: number
}

/** Calls that read one import attempt. */
export interface ImportAttemptScope extends ImportOrganizationScope {
  importAttemptId: string
}

/** Previews send no attempt id; imports send the one the preview created. */
type ImportFormDataInput<TInput> = TInput & { importAttemptId?: string }

function buildCustomerImportFormData({
  file,
  sourceSystem,
  importAttemptId,
}: ImportFormDataInput<CustomerImportFileInput>) {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('source_system', sourceSystem)

  if (importAttemptId) {
    formData.append('import_attempt_id', importAttemptId)
  }

  return formData
}

const customerImportSourceLabels: Record<CustomerImportSourceSystem, string> = {
  fsn: 'Flower Shop Network',
  the_floral_pos: 'The Floral POS',
  ftd: 'FTD',
  floranext: 'FloraNext',
  quickflora: 'QuickFlora',
  bloomnation: 'BloomNation',
  generic_customer_csv: 'Generic Customer CSV',
}

function buildProductImportFormData({
  file,
  sourceSystem,
  importScope,
  websiteNavigation,
  importAttemptId,
}: ImportFormDataInput<ProductImportFileInput>) {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('source_system', sourceSystem)
  formData.append('import_scope', importScope)
  formData.append('website_navigation', websiteNavigation)

  if (importAttemptId) {
    formData.append('import_attempt_id', importAttemptId)
  }

  return formData
}

export async function previewCustomerImport({
  organizationId,
  file,
  sourceSystem,
}: ImportOrganizationScope & CustomerImportFileInput) {
  const response = await axios.post(
    '/imports/customers/preview',
    buildCustomerImportFormData({ file, sourceSystem }),
    buildAdminRequestConfig({ organizationId })
  )

  return customerImportResponseSchema.parse(response)
}

export async function previewProductImport({
  organizationId,
  file,
  sourceSystem,
  importScope,
  websiteNavigation,
}: ImportOrganizationScope & ProductImportFileInput) {
  const response = await axios.post(
    '/imports/products/preview',
    buildProductImportFormData({ file, sourceSystem, importScope, websiteNavigation }),
    buildAdminRequestConfig({ organizationId })
  )

  return productImportResponseSchema.parse(response)
}

export async function importProductImport({
  organizationId,
  file,
  sourceSystem,
  importScope,
  websiteNavigation,
  importAttemptId,
}: ImportOrganizationScope & ProductImportInput) {
  const response = await axios.post(
    '/imports/products/import',
    buildProductImportFormData({
      file,
      sourceSystem,
      importScope,
      websiteNavigation,
      importAttemptId,
    }),
    buildAdminRequestConfig({ organizationId })
  )

  const attemptResult = productImportAttemptSchema.safeParse(response)

  if (attemptResult.success) {
    return attemptResult.data
  }

  const completedResponseResult = productImportResponseSchema.safeParse(response)

  if (completedResponseResult.success) {
    return completedProductImportAttempt({
      importAttemptId,
      response: completedResponseResult.data,
    })
  }

  throw attemptResult.error
}

export async function getProductImportAttempt({
  organizationId,
  importAttemptId,
}: ImportAttemptScope) {
  const response = await axios.get(
    `/imports/products/import-attempts/${importAttemptId}`,
    buildAdminRequestConfig({ organizationId })
  )

  return productImportAttemptSchema.parse(response)
}

export async function getActiveProductImportAttempt({ organizationId }: ImportOrganizationScope) {
  const response = await axios.get(
    '/imports/products/import-attempts/active',
    buildAdminRequestConfig({ organizationId })
  )

  return productImportActiveAttemptResponseSchema.parse(response).attempt
}

export async function importCustomerImport({
  organizationId,
  file,
  sourceSystem,
  importAttemptId,
}: ImportOrganizationScope & CustomerImportInput) {
  const response = await axios.post(
    '/imports/customers/import',
    buildCustomerImportFormData({ file, sourceSystem, importAttemptId }),
    buildAdminRequestConfig({ organizationId })
  )

  const attemptResult = customerImportAttemptSchema.safeParse(response)

  if (attemptResult.success) {
    return attemptResult.data
  }

  const completedResponseResult = customerImportResponseSchema.safeParse(response)

  if (completedResponseResult.success) {
    return completedCustomerImportAttempt({
      importAttemptId,
      response: completedResponseResult.data,
      sourceSystem,
    })
  }

  throw attemptResult.error
}

export async function getCustomerImportAttempt({
  organizationId,
  importAttemptId,
}: ImportAttemptScope) {
  const response = await axios.get(
    `/imports/customers/import-attempts/${importAttemptId}`,
    buildAdminRequestConfig({ organizationId })
  )

  return customerImportAttemptSchema.parse(response)
}

export async function getActiveCustomerImportAttempt({ organizationId }: ImportOrganizationScope) {
  const response = await axios.get(
    '/imports/customers/import-attempts/active',
    buildAdminRequestConfig({ organizationId })
  )

  return customerImportActiveAttemptResponseSchema.parse(response).attempt
}

function completedProductImportAttempt({
  importAttemptId,
  response,
}: {
  importAttemptId: string
  response: ProductImportResponse
}): ProductImportAttempt {
  const processed = response.summary.imported + response.summary.updated

  return {
    id: importAttemptId,
    status: 'completed',
    stage: 'completed',
    stage_label: 'Complete',
    message: 'Product import finished.',
    source_system: response.source_system,
    source_label: response.source_label,
    import_scope: response.import_scope,
    import_scope_label: response.import_scope_label,
    website_navigation_mode: response.website_navigation.mode,
    progress: {
      processed,
      total: processed,
      percent: 100,
    },
    result: response,
    error: null,
    queued_at: null,
    started_at: null,
    finished_at: null,
  }
}

function completedCustomerImportAttempt({
  importAttemptId,
  response,
  sourceSystem,
}: {
  importAttemptId: string
  response: CustomerImportResponse
  sourceSystem: CustomerImportSourceSystem
}): CustomerImportAttempt {
  const processed = response.summary.created

  return {
    id: importAttemptId,
    status: 'completed',
    stage: 'completed',
    stage_label: 'Complete',
    message: 'Customer import finished.',
    source_system: sourceSystem,
    source_label: customerImportSourceLabels[sourceSystem],
    progress: {
      processed,
      total: processed,
      percent: 100,
    },
    result: response,
    error: null,
    queued_at: null,
    started_at: null,
    finished_at: null,
  }
}
